import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart } from "lucide-react";
import { ETSY_SHOP } from "../data/site";
import { Reveal } from "./motion";

const STORY_IMG =
  "https://images.pexels.com/photos/8412836/pexels-photo-8412836.jpeg?auto=compress&cs=tinysrgb&w=1200";

export const Story = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="story" ref={ref} data-testid="story-section" className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-44">
      <div className="grid items-center gap-16 lg:grid-cols-12 lg:gap-24">
        <div className="relative lg:col-span-5">
          <Reveal>
            <div className="overflow-hidden rounded-t-[999px] border border-navy/20 bg-lavender">
              <motion.img
                src={STORY_IMG}
                alt="The studio behind Dream Freely"
                data-testid="story-image"
                style={{ y: yImg }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="aspect-[3/4] w-full scale-110 object-cover"
              />
            </div>
            <div className="absolute -right-6 bottom-10 border border-navy/20 bg-cream px-6 py-4 shadow-[0_20px_50px_-20px_rgba(27,42,74,0.4)] md:-right-10">
              <p className="font-caveat text-2xl font-semibold text-navy">Dream big. Live freely.</p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-playfair text-4xl leading-[0.95] tracking-tight text-navy md:text-6xl">
              A cohesive brand. Meaningful messages. Products that inspire.
            </h2>
            <p className="mt-10 max-w-xl text-sm leading-relaxed text-navy/70 md:text-lg">
              Dream Freely Studios began with a single sentence —
              <span className="font-playfair italic text-navy"> good things are allowed to happen</span> — and a belief
              that the objects we carry every day should carry something back.
            </p>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-navy/70 md:text-lg">
              Each piece starts as one of ten signature phrases, lettered in the studio, then finds its way onto totes,
              bottles, notebooks, pajamas and little luxuries. Every order is lovingly fulfilled through our Etsy shop.
            </p>
            <p className="mt-12 -rotate-2 font-caveat text-4xl font-semibold text-mustard md:text-5xl">
              make today amazing
              <Heart className="mb-2 ml-2 inline-block h-6 w-6 fill-mustard text-mustard" />
            </p>
            <a
              href={ETSY_SHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="story-shop-etsy-btn"
              className="mt-12 inline-block rounded-full bg-navy px-9 py-4 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
            >
              Visit our Etsy shop
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
